// src/components/atomic-crm/sales/SalesList.tsx
import { useMutation } from "@tanstack/react-query";
import {
  Ban,
  CheckCircle2,
  KeyRound,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Trash2,
} from "lucide-react";
import * as React from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from "ra-core";
import { CreateButton } from "@/components/ds/admin/create-button";
import { DataTable } from "@/components/ds/admin/data-table";
import { ExportButton } from "@/components/ds/admin/export-button";
import { List } from "@/components/ds/admin/list";
import { SearchInput } from "@/components/ds/admin/search-input";
import { Badge } from "@/components/ds/ui/badge";
import { Button } from "@/components/ds/ui/button";
import { Card } from "@/components/ds/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ds/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ds/ui/dropdown-menu";
import { Skeleton } from "@/components/ds/ui/skeleton";

import { TopToolbar } from "../layout/TopToolbar";
import useAppBarHeight from "../misc/useAppBarHeight";
import type { CrmDataProvider } from "../providers/types";

const SalesListActions = () => {
  const translate = useTranslate();
  return (
    <TopToolbar>
      <ExportButton />
      <CreateButton label={translate("crm.user.action.create")} />
    </TopToolbar>
  );
};

const filters = [<SearchInput source="q" alwaysOn />];

const RoleField = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  const { data: rolesRaw } = useGetList("config_roles", {
    pagination: { page: 1, perPage: 100 },
  });
  const roles = Array.isArray(rolesRaw) ? rolesRaw : rolesRaw?.data || [];

  if (!record) return null;
  const role = roles.find((r: any) => r.id === record.role_id);

  if (!role) {
    return (
      <span className="text-muted-foreground text-sm">
        {translate("crm.user.field.no_role")}
      </span>
    );
  }

  const variant = role.is_admin
    ? "destructive"
    : role.can_manage_team
      ? "default"
      : "secondary";

  return <Badge variant={variant as any}>{role.display_name}</Badge>;
};

const ManagerField = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  const { data: usersRaw } = useGetList("sales", {
    pagination: { page: 1, perPage: 100 },
  });
  const users = Array.isArray(usersRaw) ? usersRaw : usersRaw?.data || [];

  if (!record) return null;
  if (!record.manager_id) {
    return (
      <span className="text-muted-foreground text-sm">
        {translate("crm.user.field.no_manager")}
      </span>
    );
  }

  const manager = users.find((u: any) => u.id === record.manager_id);
  if (!manager) return <span className="text-muted-foreground">—</span>;

  return (
    <span className="text-sm">
      {manager.first_name} {manager.last_name}
    </span>
  );
};

const OptionsField = (_props: { label?: string | boolean }) => {
  const record = useRecordContext();
  const translate = useTranslate();

  const { data: rolesRaw } = useGetList("config_roles", {
    pagination: { page: 1, perPage: 100 },
  });
  const roles = Array.isArray(rolesRaw) ? rolesRaw : rolesRaw?.data || [];

  if (!record) return null;

  const role = roles.find((r: any) => r.id === record.role_id);
  const isAdmin = role?.is_admin ?? record.administrator;

  return (
    <div className="flex flex-row gap-1">
      {isAdmin && (
        <Badge
          variant="outline"
          className="border-blue-300 dark:border-blue-700"
        >
          {translate("crm.user.field.administrator")}
        </Badge>
      )}
      {record.disabled && (
        <Badge
          variant="outline"
          className="border-orange-300 dark:border-orange-700"
        >
          {translate("crm.user.field.disabled")}
        </Badge>
      )}
    </div>
  );
};

const ActivityField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const status = record.activity_status as string | undefined;
  const lastSignIn = record.last_sign_in_at as string | null | undefined;

  const variantMap: Record<string, any> = {
    active: "default",
    idle: "secondary",
    dormant: "destructive",
    never: "outline",
  };
  const labelMap: Record<string, string> = {
    active: "Active",
    idle: "Idle",
    dormant: "Dormant",
    never: "Never signed in",
  };

  if (!status) return <span className="text-muted-foreground">—</span>;

  return (
    <div className="flex flex-col gap-1">
      <Badge variant={variantMap[status] ?? "outline"}>
        {labelMap[status] ?? status}
      </Badge>
      {lastSignIn && (
        <span className="text-xs text-muted-foreground">
          {new Date(lastSignIn).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      )}
    </div>
  );
};

const RowActions = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const [inviteDialogOpen, setInviteDialogOpen] = React.useState(false);
  const [resetDialogOpen, setResetDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = React.useState(false);

  const { mutate: disableUser, isPending: isDisablePending } = useMutation({
    mutationFn: async () =>
      dataProvider.salesUpdate(record.id, { disabled: true }),
    onSuccess: () => {
      notify("User disabled. They can no longer sign in.", { type: "success" });
      setDisableDialogOpen(false);
      refresh();
    },
    onError: (error: any) => notify(error.message || "Failed to disable user", { type: "error" }),
  });

  const { mutate: enableUser } = useMutation({
    mutationFn: async () =>
      dataProvider.salesUpdate(record.id, { disabled: false }),
    onSuccess: () => {
      notify("User re-enabled.", { type: "success" });
      refresh();
    },
  });

  const { mutate: deleteUser, isPending: isDeletePending } = useMutation({
    mutationFn: async () => dataProvider.salesDelete(record.id),
    onSuccess: () => {
      notify("User permanently deleted.", { type: "success" });
      setDeleteDialogOpen(false);
      refresh();
    },
    onError: (error: any) => {
      console.error("Delete error:", error);
      notify(error.message || "Failed to delete user", { type: "error" });
    },
  });

  const { mutate: resendInvite, isPending: isInvitePending } = useMutation({
    mutationFn: async () => dataProvider.resendInvite(record.id),
    onSuccess: () => {
      notify(translate("crm.user.notification.invite_sent"));
      setInviteDialogOpen(false);
      refresh();
    },
    onError: () => {
      notify(translate("crm.user.notification.invite_error"), { type: "error" });
      setInviteDialogOpen(false);
    },
  });

  const { mutate: resetPassword, isPending: isResetPending } = useMutation({
    mutationFn: async () => dataProvider.resetPassword(record.id),
    onSuccess: () => {
      notify(translate("crm.user.notification.reset_sent"));
      setResetDialogOpen(false);
      refresh();
    },
    onError: () => {
      notify(translate("crm.user.notification.reset_error"), { type: "error" });
      setResetDialogOpen(false);
    },
  });

  if (!record) return null;

  const isConfirmed = record.email_confirmed_at !== null;
  const isDisabled = record.disabled === true;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <span className="sr-only">{translate("ra.action.open_menu")}</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>User actions</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem asChild>
            <a href={`#/sales/${record.id}`} className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Pencil className="mr-2 h-4 w-4" /> Edit
            </a>
          </DropdownMenuItem>

          {!isConfirmed && (
            <DropdownMenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); setInviteDialogOpen(true); }}>
              <RefreshCw className="mr-2 h-4 w-4" /> Resend Invite
            </DropdownMenuItem>
          )}
          {isConfirmed && !isDisabled && (
            <DropdownMenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); setResetDialogOpen(true); }}>
              <KeyRound className="mr-2 h-4 w-4" /> Send Password Reset
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {!isDisabled ? (
            <DropdownMenuItem
              className="text-orange-600 focus:text-orange-600"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDisableDialogOpen(true); }} // ✅ Fixed
            >
              <Ban className="mr-2 h-4 w-4" /> Disable user
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); enableUser(); }}>
              <CheckCircle2 className="mr-2 h-4 w-4" /> Re-enable user
            </DropdownMenuItem>
          )}

          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteDialogOpen(true); }} // ✅ Fixed
          >
            <Trash2 className="mr-2 h-4 w-4" /> Delete permanently
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Disable confirmation */}
      <Dialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Disable {record.first_name} {record.last_name}?</DialogTitle>
            <DialogDescription>
              They will be immediately signed out and unable to log in again. Their data and history stay intact — you can re-enable later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDisableDialogOpen(false); }}> {/* ✅ Fixed */}
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => disableUser()} disabled={isDisablePending}>
              {isDisablePending ? "Disabling..." : "Disable user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hard delete confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Delete {record.first_name} {record.last_name} permanently?</DialogTitle>
            <DialogDescription className="space-y-2">
              <p className="font-medium text-destructive">This cannot be undone.</p>
              <p>The user's auth account and sales record will be removed. Any contacts, deals, or notes they own will be reassigned to you.</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteDialogOpen(false); }}> {/* ✅ Fixed */}
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => deleteUser()} disabled={isDeletePending}>
              {isDeletePending ? "Deleting..." : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>{translate("crm.user.dialog.resend_invitation.title")}</DialogTitle>
            <DialogDescription>
              {translate("crm.user.dialog.resend_invitation.description", { email: record.email })}
              <br /><br />
              {translate("crm.user.dialog.resend_invitation.fresh_link")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={(e) => { e.stopPropagation(); setInviteDialogOpen(false); }} disabled={isInvitePending}>
              {translate("crm.user.action.cancel")}
            </Button>
            <Button onClick={(e) => { e.stopPropagation(); resendInvite(); }} disabled={isInvitePending}>
              {isInvitePending ? translate("crm.user.action.sending") : translate("crm.user.action.send_invitation")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>{translate("crm.user.dialog.reset_password.title")}</DialogTitle>
            <DialogDescription>
              {translate("crm.user.dialog.reset_password.description", { email: record.email })}
              <br /><br />
              {translate("crm.user.dialog.reset_password.reset_link")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={(e) => { e.stopPropagation(); setResetDialogOpen(false); }} disabled={isResetPending}>
              {translate("crm.user.action.cancel")}
            </Button>
            <Button onClick={(e) => { e.stopPropagation(); resetPassword(); }} disabled={isResetPending}>
              {isResetPending ? translate("crm.user.action.sending") : translate("crm.user.action.send_reset_link")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export function SalesList() {
  return (
    <List
      filters={filters}
      actions={<SalesListActions />}
      sort={{ field: "first_name", order: "ASC" }}
    >
      <SalesListLayout />
    </List>
  );
}

const SalesListLayout = () => {
  const { data, isPending, filterValues } = useListContext();
  const { identity } = useGetIdentity();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (!identity || isPending) {
    return (
      <Card className="p-4">
        <div className="space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </Card>
    );
  }

  if (!data?.length && !hasFilters) {
    return <SalesEmpty />;
  }

  return (
    <DataTable>
      <DataTable.Col source="first_name" />
      <DataTable.Col source="last_name" />
      <DataTable.Col source="email" />
      <DataTable.Col label="crm.user.field.role">
        <RoleField />
      </DataTable.Col>
      <DataTable.Col label="crm.user.field.manager">
        <ManagerField />
      </DataTable.Col>
      <DataTable.Col label={false}>
        <OptionsField />
      </DataTable.Col>
      <DataTable.Col label={false}>
        <RowActions />
      </DataTable.Col>
      <DataTable.Col label="crm.user.field.last_sign_in">
        <ActivityField />
      </DataTable.Col>
    </DataTable>
  );
};

const SalesEmpty = () => {
  const appbarHeight = useAppBarHeight();
  const translate = useTranslate();
  return (
    <div
      className="flex flex-col justify-center items-center gap-3"
      style={{ height: `calc(100dvh - ${appbarHeight}px)` }}
    >
      <img src="./img/empty.svg" alt={translate("crm.user.empty.title")} />
      <div className="flex flex-col gap-0 items-center">
        <h6 className="text-lg font-bold">
          {translate("crm.user.empty.title")}
        </h6>
        <p className="text-sm text-muted-foreground text-center mb-4">
          {translate("crm.user.empty.description")}
        </p>
      </div>
      <div className="flex flex-row gap-2">
        <CreateButton label={translate("crm.user.action.create")} />
      </div>
    </div>
  );
};